import { useEffect } from 'react';

interface SeoHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  schema?: object | object[];
}

export default function SeoHead({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  schema,
}: SeoHeadProps) {
  useEffect(() => {
    // Title
    document.title = title;

    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    if (canonical) setLink('canonical', canonical);

    // OG
    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    if (canonical) setMeta('og:url', canonical, true);

    // Twitter
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);

    // Schema
    if (schema) {
      const schemas = Array.isArray(schema) ? schema : [schema];
      // Remove old page-level schemas (not the global ones in index.html)
      document.querySelectorAll('script[data-page-schema]').forEach((el) => el.remove());
      schemas.forEach((s) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-page-schema', 'true');
        script.textContent = JSON.stringify(s);
        document.head.appendChild(script);
      });
    }

    return () => {
      document.querySelectorAll('script[data-page-schema]').forEach((el) => el.remove());
    };
  }, [title, description, keywords, canonical, ogType, schema]);

  return null;
}
